<ng-container *ngIf="showTripsTable$ | async; else loader">
  <ng-container *ngTemplateOutlet="tripsGrid"></ng-container>
</ng-container>

<ng-template #tripsGrid>
  <div class="flex flex--column ms-medium-b" *ngIf="trips$ | async as trips">
    <div class="ms-large-b">
      <fcom-radio-item-group
        [options]="[{ value: TripStatus.UPCOMING }, { value: TripStatus.PAST }]"
        [controlName]="'status'"
        [isHorizontal]="true"
        [titleText]="false"
        [translationPrefix]="'CORPORATE.corporatePortal.tripsView.status'"
        [parentForm]="form"
      >
      </fcom-radio-item-group>
    </div>
    <div class="flex trip-filters">
      <div class="ms-small-r search">
        <fcom-text-input
          #searchTerm
          [parentForm]="form"
          [controlName]="'searchTerm'"
          [displayMargin]="false"
          [placeHolder]="'CORPORATE.corporatePortal.tripsView.searchTrips'"
          [hideLabel]="true"
          [disableAutoComplete]="true"
          [clearInput]="true"
        ></fcom-text-input>
      </div>
      <div class="ms-small-r filter">
        <fin-select-input
          [parentForm]="form"
          [controlName]="'filter'"
          [options]="dateRangeOptions$ | async"
          [disableLabel]="true"
          [disableSelectOption]="true"
          [displayMargin]="false"
          [selectLabelTranslationKey]="'CORPORATE.corporatePortal.tripsView.filterTrips.placeholder'"
          [translationPrefix]="'CORPORATE.corporatePortal.tripsView.filterTrips'"
        ></fin-select-input>
      </div>
      <div class="flex--self-center flex-1 my-trips" *ngIf="isAdminOrTravelBooker$ | async">
        <fcom-checkbox (change)="showMyTrips$.next($event)">
          <span class="font-body-2">
            {{ 'CORPORATE.corporatePortal.tripsView.showMyTrips' | finLocalization }}
          </span>
        </fcom-checkbox>
      </div>
    </div>
    <ng-container *ngIf="(trips$ | async).length || resultsLoading; else noResults">
      <div class="ms-medium-t relative" #tripsWrapper>
        <div class="dn-laptop-down">
          <ng-container *ngIf="resultsLoading">
            <ng-container *ngTemplateOutlet="tableLoader"></ng-container>
          </ng-container>

          <table class="fill font-body-2">
            <caption class="is-vishidden">
              {{
                'CORPORATE.corporatePortal.trips.title' | finLocalization
              }}
            </caption>
            <thead>
              <tr class="align-left">
                <th class="border-top border-bottom border-left rounded-top-left-large col-40"></th>
                <th class="border-top border-bottom ps-xsmall-x ps-small-y">
                  {{ 'CORPORATE.corporatePortal.tripsView.table.trips' | finLocalization }}
                </th>
                <th class="border-top border-bottom ps-xsmall-x ps-small-y">
                  {{ 'CORPORATE.corporatePortal.tripsView.table.date' | finLocalization }}
                </th>
                <th class="border-top border-bottom ps-xsmall-x ps-small-y">
                  {{ 'CORPORATE.corporatePortal.tripsView.table.departure' | finLocalization }}
                </th>
                <th class="border-top border-bottom ps-xsmall-x ps-small-y">
                  {{ 'CORPORATE.corporatePortal.tripsView.table.passengers' | finLocalization }}
                </th>
                <th class="border-top border-bottom ps-xsmall-x ps-small-y">
                  {{ 'CORPORATE.corporatePortal.tripsView.table.bookingRef' | finLocalization }}
                </th>
                <th class="border-top border-bottom border-right rounded-top-right-large"></th>
              </tr>
            </thead>
            <tbody>
              @for (tripRow of trips; track tripRow) {
                <tr data-hj-suppress>
                  <td class="border-left border-bottom ps-xsmall-x toggle-expand">
                    <div class="flex">
                      <fcom-icon-button
                        *ngIf="tripRow.passengers.length > 1 || tripRow.tripType !== TripType.ONEWAY"
                        class="font-body-2 block"
                        data-testid="row-expander-desktop"
                        [class.expanded]="tripRow.expanded"
                        [size]="IconButtonSize.SMALL"
                        [theme]="IconButtonTheme.LIGHT_TERTIARY_GHOST"
                        [icon]="{ name: SvgLibraryIcon.CHEVRON_DOWN }"
                        [aria]="{ label: 'CORPORATE.corporatePortal.tripsView.aria.expandRow' | finLocalization }"
                        (clickBtn)="tripRow.expanded = !tripRow.expanded"
                      ></fcom-icon-button>
                    </div>
                  </td>
                  <td class="bold-type border-bottom ps-xsmall-x ps-small-y">
                    <ng-container *ngTemplateOutlet="locations; context: { trip: tripRow }"></ng-container>
                  </td>
                  <td class="border-bottom ps-xsmall-x ps-small-y">
                    {{ tripRow.departure.dateTime | finDateFormat: 'DATE' }}
                  </td>
                  <td class="border-bottom ps-xsmall-x ps-small-y">{{ tripRow.departure.dateTime | finTime }}</td>
                  <td class="border-bottom ps-xsmall-x ps-small-y">
                    <span *ngIf="tripRow.passengers.length > 1">
                      {{
                        'CORPORATE.corporatePortal.tripsView.table.passengersField'
                          | finLocalization: { amount: tripRow.passengers.length }
                      }}
                    </span>
                    <span *ngIf="tripRow.passengers.length === 1">
                      {{ tripRow.passengers[0].firstName + ' ' + tripRow.passengers[0].lastName | titlecase }}
                    </span>
                  </td>
                  <td class="border-bottom ps-xsmall-x ps-small-y">{{ tripRow.bookingReference }}</td>
                  <td class="border-bottom border-right ps-xsmall-x">
                    <div class="flex">
                      <ng-container *ngTemplateOutlet="actions; context: { trip: tripRow }"></ng-container>
                    </div>
                  </td>
                </tr>
                <tr
                  class="details-row nordic-blue-25-bg"
                  [class.opened]="tripRow.expanded"
                  [attr.aria-hidden]="!tripRow.expanded"
                >
                  <td class="border-left" [class.border-bottom]="tripRow.expanded"></td>
                  <td [class.border-bottom]="tripRow.expanded" class="overflow-hidden" data-hj-suppress>
                    <div
                      *ngIf="tripRow.tripType !== TripType.ONEWAY"
                      [ngClass]="tripRow.expanded ? 'ps-small-y' : ''"
                      class="ps-xsmall-x"
                    >
                      <div *ngFor="let bound of tripRow.bounds">
                        <ng-container
                          *ngTemplateOutlet="location; context: { location: bound.departure }"
                        ></ng-container>
                        <fcom-icon
                          class="icon-small ms-xxsmall-x"
                          [name]="SvgLibraryIcon.AIRCRAFT_HORIZONTAL"
                        ></fcom-icon>
                        <ng-container *ngTemplateOutlet="location; context: { location: bound.arrival }"></ng-container>
                      </div>
                    </div>
                  </td>
                  <td [class.border-bottom]="tripRow.expanded" class="overflow-hidden" data-hj-suppress>
                    <div
                      *ngIf="tripRow.tripType !== TripType.ONEWAY"
                      [ngClass]="tripRow.expanded ? 'ps-small-y' : ''"
                      class="ps-xsmall-x"
                    >
                      <div *ngFor="let bound of tripRow.bounds">
                        {{ bound.flights[0].departure.dateTime | finDateFormat: 'DATE' }}
                      </div>
                    </div>
                  </td>
                  <td [class.border-bottom]="tripRow.expanded" class="overflow-hidden" data-hj-suppress>
                    <div
                      *ngIf="tripRow.tripType !== TripType.ONEWAY"
                      [ngClass]="tripRow.expanded ? 'ps-small-y' : ''"
                      class="ps-xsmall-x"
                    >
                      <div *ngFor="let bound of tripRow.bounds">
                        {{ bound.flights[0].departure.dateTime | finTime }}
                      </div>
                    </div>
                  </td>
                  <td [class.border-bottom]="tripRow.expanded" class="overflow-hidden" data-hj-suppress>
                    <div [ngClass]="tripRow.expanded ? 'ps-small-y' : ''" class="ps-xsmall-x">
                      <span *ngIf="tripRow.passengers.length > 1" class="comma-separated">
                        <span *ngFor="let pax of tripRow.passengers">{{
                          pax.firstName + ' ' + pax.lastName | titlecase
                        }}</span>
                      </span>
                    </div>
                  </td>
                  <td [class.border-bottom]="tripRow.expanded"></td>
                  <td [class.border-bottom]="tripRow.expanded" class="border-right"></td>
                </tr>
              }
            </tbody>
          </table>
          <fcom-pagination
            #paginationComponent
            [totalItems]="totalAmount"
            [itemsPerPageOptions]="itemsPerPageOptions"
            [(itemsPerPage)]="itemsPerPage"
            (indexesChange)="changePage($event)"
          ></fcom-pagination>
        </div>
        <div class="dn-laptop-up">
          <ng-container *ngIf="resultsLoading">
            <ng-container *ngTemplateOutlet="tableLoader"></ng-container>
          </ng-container>

          <ul class="no-list-style">
            @for (tripRow of trips; track tripRow; let first = $first) {
              <li
                class="border-left border-right border-bottom"
                [class.border-top]="first"
                [class.rounded-top-large]="first"
              >
                <button (click)="openTripDetails(tripRow)" class="flex fill ps-small-x ps-xsmall-y">
                  <ng-container *ngTemplateOutlet="mobileTrip; context: { trip: tripRow }"></ng-container>
                </button>
              </li>
            }
          </ul>

          <fcom-pagination
            [totalItems]="totalAmount"
            [itemsPerPageOptions]="itemsPerPageOptions"
            [(itemsPerPage)]="itemsPerPage"
            (indexesChange)="changePage($event)"
          ></fcom-pagination>
        </div>
      </div>
    </ng-container>
  </div>
</ng-template>

<ng-template #mobileTrip let-trip="trip">
  <div class="align-left">
    <div class="bold-type" data-hj-suppress>
      <ng-container *ngTemplateOutlet="locations; context: { trip }"></ng-container>
    </div>
    <div data-hj-suppress>
      {{ trip.departure.dateTime | finDateFormat: 'DATE' }}
      @if (trip.bounds.length > 1) {
        <span> - </span>
        {{ trip.arrival.dateTime | finDateFormat: 'DATE' }}
      }
    </div>
    <div data-hj-suppress>
      @if (trip.passengers.length > 1) {
        {{ trip.passengers.length }}
        {{ 'CORPORATE.corporatePortal.tripsView.passengers' | finLocalization }}
      } @else {
        <span class="comma-separated">
          <span *ngFor="let pax of trip.passengers">{{ pax.firstName + ' ' + pax.lastName | titlecase }}</span>
        </span>
      }
    </div>
  </div>
  <div class="flex flex-1 flex--end ps-small-r">
    <fcom-tag>
      {{ 'CORPORATE.corporatePortal.tripType.' + trip.tripType.toLowerCase() | finLocalization }}
    </fcom-tag>
  </div>
  <div class="flex--self-center">
    <fcom-icon [name]="SvgLibraryIcon.CHEVRON_RIGHT"></fcom-icon>
  </div>
</ng-template>

<ng-template #location let-location="location">
  <span>{{ location.locationTitle || location.locationCode }}</span>
</ng-template>

<ng-template #locations let-trip="trip">
  <ng-container *ngTemplateOutlet="location; context: { location: trip.bounds[0].departure }"></ng-container>
  <ng-container [ngSwitch]="trip.tripType">
    <fcom-icon
      *ngSwitchCase="TripType.RETURN"
      class="icon-small ms-xxsmall-x"
      [name]="SvgLibraryIcon.RETURN_TRIP"
    ></fcom-icon>
    <fcom-icon *ngSwitchDefault class="icon-small ms-xxsmall-x" [name]="SvgLibraryIcon.AIRCRAFT_HORIZONTAL"></fcom-icon>
  </ng-container>
  <ng-container *ngTemplateOutlet="location; context: { location: trip.bounds[0].arrival }"></ng-container>
  <ng-container *ngIf="trip.tripType === TripType.MULTICITY">
    (+{{ trip.bounds.length - 1 }} {{ 'CORPORATE.corporatePortal.tripsView.flights' | finLocalization }})</ng-container
  >
</ng-template>

<ng-template #noResults>
  <div class="grey-25-bg align-center pr-xlarge-y rounded-large font-body-1" data-testid="no-results">
    <ng-container *ngIf="hasActiveFilters$ | async">
      {{ 'CORPORATE.corporatePortal.tripsView.noMatches' | finLocalization }}
    </ng-container>
    <ng-container *ngIf="(hasActiveFilters$ | async) === false && (isAdminOrTravelBooker$ | async) === true">
      {{ 'CORPORATE.corporatePortal.tripsView.noUpcomingTeamFlights' | finLocalization }}
    </ng-container>
    <ng-container *ngIf="(hasActiveFilters$ | async) === false && (isAdminOrTravelBooker$ | async) === false">
      {{ 'CORPORATE.corporatePortal.tripsView.noUpcomingFlights' | finLocalization }}
    </ng-container>
  </div>
</ng-template>

<ng-template #loader>
  <div class="pr-xlarge-y">
    <fcom-loader [loaderType]="LoaderTheme.INLINE"></fcom-loader>
  </div>
</ng-template>

<ng-template #tableLoader>
  <div class="absolute fill grow z1 grey-25-bg border rounded-large flex flex--center">
    <fcom-loader [loaderType]="LoaderTheme.INLINE"></fcom-loader>
  </div>
</ng-template>

<ng-template #actions let-trip="trip">
  <fcom-icon-button
    class="font-body-2 block"
    [disabled]="trip.loading"
    [size]="IconButtonSize.MEDIUM"
    [title]="'CORPORATE.corporatePortal.tripsView.aria.rebookTrip' | finLocalization"
    [theme]="IconButtonTheme.LIGHT_TERTIARY_GHOST"
    [icon]="{ name: SvgLibraryIcon.UPDATE_CART }"
    [aria]="{ label: 'CORPORATE.corporatePortal.tripsView.aria.rebookTrip' | finLocalization }"
    (clickBtn)="openRebookModal(trip)"
  >
  </fcom-icon-button>
  <fcom-icon-button
    class="font-body-2 block"
    [disabled]="trip.loading"
    [size]="IconButtonSize.MEDIUM"
    [title]="'CORPORATE.corporatePortal.tripsView.aria.downloadVAT' | finLocalization"
    [theme]="IconButtonTheme.LIGHT_TERTIARY_GHOST"
    [icon]="{ name: SvgLibraryIcon.VAT_RECEIPT_DOWNLOAD }"
    [aria]="{ label: 'CORPORATE.corporatePortal.tripsView.aria.downloadVAT' | finLocalization }"
    (clickBtn)="downloadVat(trip)"
  ></fcom-icon-button>
  <fcom-icon-button
    *ngIf="form.controls.status.value === TripStatus.UPCOMING"
    class="font-body-2 block"
    data-testid="manage-booking-button"
    [disabled]="trip.loading"
    [size]="IconButtonSize.MEDIUM"
    [theme]="IconButtonTheme.LIGHT_TERTIARY_GHOST"
    [title]="'CORPORATE.corporatePortal.tripsView.aria.manageBooking' | finLocalization"
    [icon]="{ name: SvgLibraryIcon.ARROW_RIGHT }"
    [aria]="{ label: 'CORPORATE.corporatePortal.tripsView.aria.manageBooking' | finLocalization }"
    (clickBtn)="manageBooking(trip)"
  ></fcom-icon-button>
</ng-template>

<fcom-drawer
  [(open)]="drawerOpen"
  [direction]="DrawerDirection.FROM_BOTTOM"
  [title]="'CORPORATE.corporatePortal.tripsView.mobile.drawerTitle' | finLocalization"
  [showButtons]="true"
>
  <div *ngIf="selectedTrip" class="ps-large nordic-blue-900-text font-body-2 fill">
    <h2 class="font-heading-5 ms-xsmall-b" data-hj-suppress>
      <ng-container *ngTemplateOutlet="locations; context: { trip: selectedTrip }"></ng-container>
    </h2>
    <fcom-tag class="ms-medium-b">
      {{ 'CORPORATE.corporatePortal.tripType.' + selectedTrip.tripType.toLowerCase() | finLocalization }}
    </fcom-tag>
    @for (bound of selectedTrip.bounds; track bound.departure.dateTime) {
      <div class="border-bottom ps-xsmall-y" data-hj-suppress>
        <fcom-icon [name]="SvgLibraryIcon.AIRCRAFT_HORIZONTAL_BLOCK"></fcom-icon>
        <span class="bold-type">
          {{ bound.departure.locationTitle || bound.departure.locationCode }}
          -
          {{ bound.arrival.locationTitle || bound.arrival.locationCode }}
        </span>
        <div class="flex ps-xsmall-t">
          <div class="fill--50">{{ bound.departure.dateTime | finDateFormat: 'DATE_WITH_TIME' }}</div>
          <div class="fill--50">{{ bound.arrival.dateTime | finDateFormat: 'DATE_WITH_TIME' }}</div>
        </div>
      </div>
    }
    <div class="flex ps-medium-t">
      <div class="fill--50">
        <h4 class="font-body-2">
          {{ 'CORPORATE.corporatePortal.tripsView.table.passengers' | finLocalization }}
          ({{ selectedTrip.passengers.length }}):
        </h4>
        <span class="comma-separated" data-hj-suppress>
          @for (pax of selectedTrip.passengers; track pax.firstName + pax.lastName) {
            <span>{{ pax.firstName + ' ' + pax.lastName | titlecase }}</span>
          }
        </span>
      </div>
      <div class="fill--50">
        <h4 class="font-body-2">{{ 'travelClasses' | finLocalization }}:</h4>
        <span>{{ 'travelClass.' + selectedTrip.bookingClass.toLowerCase() | finLocalization }}</span>
      </div>
    </div>
    <div class="ps-medium-t">
      <h4 class="font-body-2">{{ 'CORPORATE.corporatePortal.tripsView.table.bookingRef' | finLocalization }}:</h4>
      <span data-hj-suppress>{{ selectedTrip.bookingReference }}</span>
    </div>
  </div>
  <div buttons class="ps-large">
    <fcom-button
      data-testid="drawer-mb-button"
      [text]="'CORPORATE.corporatePortal.tripsView.mobile.manageButton' | finLocalization"
      [mode]="ButtonMode.FILL"
      [icon]="{ name: SvgLibraryIcon.ARROW_RIGHT, position: IconPosition.RIGHT }"
      (clickBtn)="manageBooking(selectedTrip)"
    ></fcom-button>
    <div class="flex ps-medium-t drawer-button-container">
      <div class="fill--50">
        <fcom-button
          data-testid="drawer-book-again-button"
          [text]="'CORPORATE.corporatePortal.tripsView.mobile.bookAgainButton' | finLocalization"
          [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
          [mode]="ButtonMode.FILL"
          (clickBtn)="openRebookModal(selectedTrip)"
        ></fcom-button>
      </div>
      <div class="fill--50">
        <fcom-button
          data-testid="drawer-vat-button"
          [text]="'CORPORATE.corporatePortal.tripsView.mobile.receiptButton' | finLocalization"
          [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
          [mode]="ButtonMode.FILL"
          (clickBtn)="downloadVat(selectedTrip)"
        ></fcom-button>
      </div>
    </div>
  </div>
</fcom-drawer>

<fin-rebook-trip #rebookTrip></fin-rebook-trip>
